import { Component, Input, OnInit } from '@angular/core';
import {InitialInterviewPage, OrderRateType, PayTransparencyCategory, PlacementType} from "../../../enums";
import { PageService } from "../service/page-service";
import { FormBuilder, FormGroup } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { InitialInterviewApiService } from "../initial-interview-api.service";
import { TalentHappyPathDataService } from "../../shared/service/talent-happy-path-data-service";
import { OrderPayTransparencyData } from '../../../../typings';
import { PayTransparencyService } from "../service/payTransparency-service";
import { isNumbersEqual } from '../util';
import { VideoInterviewFlowService } from "../video-interview/service/video-interview-flow.service";

@Component({
  selector: 'app-pay-transparency',
  templateUrl: './pay-transparency.component.html',
  standalone: false,
  styleUrl: './pay-transparency.component.scss'
})
export class PayTransparencyComponent implements OnInit {

  @Input() returningCandidate: boolean;
  isLaterModalVisible: boolean = false;
  isSubmitModalVisible: boolean = false;
  payTransparencyForm: FormGroup;
  payRateHeader: string;
  payCategoryId: number;
  buttonTextKey: string = 'talent-happy-path.buttons.submit';
  hideDoThisLaterButton: boolean = false;
  payTransparencyResponseData: number[] = [];
  orderId = this.talentHappyPathDataService.postingInfo.orderId;
  lowRespondentThreshold = 4; // TODO: arbitrary value until product weighs in
  payTransparencyData: OrderPayTransparencyData;
  minPay: number;
  maxPay: number;

  constructor(private pageService: PageService,
              private translateService: TranslateService,
              private fb: FormBuilder,
              private initialInterviewApiService: InitialInterviewApiService,
              private talentHappyPathDataService: TalentHappyPathDataService,
              private payTransparencyService: PayTransparencyService,
              private videoInterviewFlowService: VideoInterviewFlowService) {
    this.payTransparencyForm = this.fb.group({
      payRate: [''],
    });
  }

  ngOnInit() {
    this.readPayTransparencyResponseData();
    if (this.returningCandidate) {
      this.hideDoThisLaterButton = true;
      this.initialInterviewApiService.readTalentPayTransparencyRate(this.talentHappyPathDataService.talent.personId, this.talentHappyPathDataService.postingInfo.orderId).subscribe(payRate => {
        this.payTransparencyForm.get('payRate').setValue(payRate?.toString());
        this.formatPayRate();
        this.buttonTextKey = 'talent-happy-path.buttons.next';
      });
    }

    // Handle fetching OrderPayTransparencyData
    if (this.payTransparencyService.getPayTransparencyData() != null) {
      this.setPayTransparencyData(this.payTransparencyService.getPayTransparencyData());
    } else {
      this.initialInterviewApiService.readTalentPayTransparencyData(
        this.talentHappyPathDataService.talent.personId,
        this.talentHappyPathDataService.postingInfo.orderId
      ).subscribe(data => {
        if (data) {
          this.payTransparencyService.setPayTransparencyData(data);
          this.setPayTransparencyData(data);
        }
      }, error => {
        console.error('Error fetching pay transparency data:', error);
      });
    }
  }

  navigateToQuestionsPage(): void {
    this.pageService.setActivePage(InitialInterviewPage.QUESTIONS_PAGE);
  }

  /** "I'll do this later" on pay transparency: same next step as submitting it (without saving a rate). */
  clickIllDoThisLater(): void {
    this.proceedAfterPayTransparency();
  }

  /**
   * The step after pay transparency (submit or "I'll do this later"):
   *  - Workflow B: the video interview (before the IIQ questions).
   *  - Workflow A / no video: the IIQ (questions for a fresh applicant, splash for a returning one).
   */
  private proceedAfterPayTransparency(): void {
    if (this.videoInterviewFlowService.shouldShowForWorkflowB()) {
      this.videoInterviewFlowService.enterVideoInterview(
        this.returningCandidate ? InitialInterviewPage.SPLASH_PAGE : InitialInterviewPage.QUESTIONS_PAGE);
    } else if (!this.returningCandidate) {
      this.navigateToQuestionsPage();
    } else {
      this.pageService.setActivePage(InitialInterviewPage.SPLASH_PAGE);
    }
  }
  filterPayRateInput(): void {
    const value = this.payTransparencyForm.get('payRate').value;
    // Only allow digits, spaces, and decimal points (no commas during input)
    let filteredValue = value.replace(/[^0-9 .]/g, '');
    this.payTransparencyForm.get('payRate').setValue(filteredValue);
    this.buttonTextKey = 'talent-happy-path.buttons.submit';
  }

  formatPayRate(): void {
    const control = this.payTransparencyForm.get('payRate');
    let value = control.value;

    if (value) {
      value = value.replace(/[ ,]/g, '');
      // Check if we have more than 2 decimal places
      if (value.includes('.')) {
        const parts = value.split('.');
        if (parts[1] && parts[1].length > 2) {
          // Round to 2 decimal places
          const rounded = Math.round(parseFloat(value) * 100) / 100;
          value = rounded.toString();
        }
      }

      // Format the number with commas for thousands
      const parts = value.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const formattedValue = parts.join('.');

      control.setValue(formattedValue);
    }
  }

  submitPayTransparency(): void {
    const rawPayRate = this.payTransparencyForm.get('payRate').value.replace(/,/g, '');
    this.initialInterviewApiService.saveTalentPayTransparencyRate(this.talentHappyPathDataService.talent.personId,
                                                                  this.talentHappyPathDataService.postingInfo.orderId,
                                                                  this.talentHappyPathDataService.postingId,
                                                                  rawPayRate
    ).subscribe(() => {
      this.proceedAfterPayTransparency();
    });
  }

  readPayTransparencyResponseData(): void {
    let orderId = this.talentHappyPathDataService.postingInfo.orderId;
    this.initialInterviewApiService.readTalentPayTransparencyResponseData(orderId).subscribe((response) => {
      if (response && response.length > 0) {
        this.payTransparencyResponseData = response;
      }
      return [];
      }, (error) => {
      console.error('Error fetching pay transparency response data:', error);
      return [];
    });
  }

  setPayTransparencyData(data: OrderPayTransparencyData): void {
    this.payTransparencyData = data;
    this.payCategoryId = data.payTransparencyCategoryId;
    this.minPay = data.minRate;
    this.maxPay = data.maxRate;

    // Set header text based on pay category
    if (this.payCategoryId === PayTransparencyCategory.HOURLY) {
      this.payRateHeader = this.translateService.instant('talent-happy-path.pay-transparency.pay-rate.hourly');
    } else if (this.payCategoryId === PayTransparencyCategory.DAILY) {
      this.payRateHeader = this.translateService.instant('talent-happy-path.pay-transparency.pay-rate.daily');
    } else if (this.payCategoryId === PayTransparencyCategory.SALARY) {
      this.payRateHeader = this.translateService.instant('talent-happy-path.pay-transparency.pay-rate.salary');
      this.minPay = data.minSalary;
      this.maxPay = data.maxSalary;
    } else if (this.payCategoryId === PayTransparencyCategory.PROJECT) {
      this.payRateHeader = this.translateService.instant('talent-happy-path.pay-transparency.pay-rate.project');
    }

    // A guard to handle cases where min and max pay are equal, but we navigated here anyway.
    // If min and max pay are equal, skip this page.
    if (isNumbersEqual(this.minPay, this.maxPay)) {
      this.pageService.setActivePage(InitialInterviewPage.SPLASH_PAGE);
    }
  }
}
